import React, { useState, useCallback, useMemo } from 'react';
import { Pin, PinOff, X, RefreshCw, Settings, ChevronDown, ChevronRight } from 'lucide-react';
import {
  BrokerState,
  BrokerPosition,
  BrokerOrder,
  BrokerBalance,
} from '../../store/brokerStore';
import { useAccountStatsStore } from '../../store/accountStatsStore';
import { calculatePnL, roundToTick } from '../../config/instruments';
import { List, RowComponentProps } from 'react-window';
import AutoBeBadge from '../AutoBeBadge';
import { RestrainBadge, useRestrainFor } from '../Restrain';
import { restrainLockMessage } from '../../store/restrainStore';

type TabType = 'positions' | 'orders' | 'accounts';

const POSITION_ROW_HEIGHT = 42;
const ORDER_ROW_HEIGHT = 42;
const MAX_LIST_HEIGHT = 350;

/**
 * Shortens account name to prefix + last 4 digits for display
 * e.g., "MFFUSFCRFLX202783055" → "MFFU...3055"
 *       "TDFYSL50742259906" → "TDFY...9906"
 */
function shortenAccountName(name: string): string {
  if (name.length <= 12) return name;
  const prefix = name.slice(0, 4);
  const suffix = name.slice(-4);
  return `${prefix}...${suffix}`;
}

interface PositionsExpandedProps {
  brokers: BrokerState[];
  positions: BrokerPosition[];
  orders: BrokerOrder[];
  totalBalance: number;
  totalPnL: number;
  currentPrice: number;
  symbol: string;
  isPinned: boolean;
  onTogglePin: () => void;
  onClose: () => void;
  onRefresh: () => void;
  onFlatten: (position: BrokerPosition) => void;
  onFlattenAll: () => void;
  onCancelOrder: (order: BrokerOrder) => void;
  onCancelAllOrders: () => void;
  onModifyOrder: (orderId: number, newPrice: number, isStopPrice: boolean, brokerId?: string, accountId?: number, skipDebounce?: boolean) => void;
  onPartialExit: (position: BrokerPosition, quantity: number) => void;
  onOpenSettings?: () => void;
}

// Helper to get broker prefix
const getBrokerPrefix = (brokerId: string): string => {
  const prefixes: Record<string, string> = {
    'mffu': 'MFFU',
    'lucid': 'LUC',
    'apex': 'APEX',
    'alpha': 'ALPH',
  };
  return prefixes[brokerId.toLowerCase()] || brokerId.slice(0, 4).toUpperCase();
};

// Helper to get account display name (broker prefix + short account ID)
const getAccountDisplayName = (
  brokerId: string,
  accountId: number,
  brokers: BrokerState[]
): string => {
  const broker = brokers.find(b => b.id === brokerId);
  const account = broker?.accounts?.find(a => a.id === accountId);
  const accountName = account?.name || String(accountId);

  // Extract last 4 digits of account name for short display
  const shortId = accountName.length > 4 ? accountName.slice(-4) : accountName;
  const prefix = getBrokerPrefix(brokerId);

  return `${prefix}:${shortId}`;
};

// Helper to format currency
const formatCurrency = (value: number | undefined, showDecimals = true) => {
  if (value === undefined || isNaN(value)) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  }).format(value);
};

// Find TP/SL orders for a position
const findOrdersForPosition = (
  orders: BrokerOrder[],
  position: BrokerPosition
): { tpOrders: BrokerOrder[]; slOrders: BrokerOrder[] } => {
  const isLong = position.netPos > 0;
  const exitAction = isLong ? 'Sell' : 'Buy';

  const workingOrders = orders.filter(
    (o) =>
      o.accountId === position.accountId &&
      o.brokerId === position.brokerId &&
      o.action === exitAction &&
      (o.ordStatus === 'Working' || o.ordStatus === 'Accepted' || o.ordStatus === 'Pending')
  );

  const tpOrders = workingOrders.filter(
    (o) => o.ordType?.toLowerCase().includes('limit') && !o.ordType?.toLowerCase().includes('stop') && o.price
  );

  const slOrders = workingOrders.filter(
    (o) => o.ordType?.toLowerCase().includes('stop') && o.stopPrice
  );

  return { tpOrders, slOrders };
};

interface PositionRowProps {
  positions: BrokerPosition[];
  orders: BrokerOrder[];
  brokers: BrokerState[];
  currentPrice: number;
  symbol: string;
  onFlatten: (position: BrokerPosition) => void;
  onBreakEven: (position: BrokerPosition) => void;
  onPlaySafe: (position: BrokerPosition) => void;
  onSellHalf: (position: BrokerPosition) => void;
}

interface OrderRowProps {
  orders: BrokerOrder[];
  brokers: BrokerState[];
  onCancelOrder: (order: BrokerOrder) => void;
}

export const PositionRow = ({
  index, style, positions, orders, brokers, currentPrice, symbol,
  onFlatten, onBreakEven, onPlaySafe, onSellHalf,
}: RowComponentProps<PositionRowProps>) => {
  const position = positions[index];
  const isLong = position.netPos > 0;
  const qty = Math.abs(position.netPos);
  const pnl = calculatePnL(
    position.netPrice,
    currentPrice,
    position.netPos,
    position.symbol || symbol
  ).dollars;
  const isInProfit = pnl > 0;
  const isInLoss = pnl < 0;
  const { tpOrders, slOrders } = findOrdersForPosition(orders, position);
  const { entry: restrain } = useRestrainFor({ ...position, fallbackSymbol: symbol });
  const restrainMsg = restrain ? restrainLockMessage(restrain) : null;
  const canSellHalf = qty > 1 && !restrainMsg;

  return (
    <div style={style}>
      <div className={`abp-position-row ${isLong ? 'long' : 'short'}`}>
        <div className="abp-position-info">
          <span className="abp-position-broker" title={`${position.brokerId}:${position.accountId}`}>
            {getAccountDisplayName(position.brokerId, position.accountId, brokers)}
          </span>
          <span className={`abp-position-side ${isLong ? 'long' : 'short'}`}>
            {isLong ? 'L' : 'S'}
          </span>
          <span className="abp-position-qty">x{qty}</span>
          <span className="abp-position-entry">
            @{position.netPrice.toFixed(2)}
          </span>
          <span className={`abp-position-pnl ${pnl >= 0 ? 'positive' : 'negative'}`}>
            {pnl >= 0 ? '+' : ''}{pnl.toFixed(0)}
          </span>
          <AutoBeBadge position={position} orders={orders} />
          <RestrainBadge position={position} fallbackSymbol={symbol} />
        </div>
        <div className="abp-position-controls">
          <button
            className={`abp-ctrl-btn ${!isInProfit || slOrders.length === 0 ? 'disabled' : ''}`}
            onClick={() => onBreakEven(position)}
            disabled={!isInProfit || slOrders.length === 0}
            title="Break Even"
          >
            BE
          </button>
          <button
            className={`abp-ctrl-btn ${!isInLoss || tpOrders.length === 0 ? 'disabled' : ''}`}
            onClick={() => onPlaySafe(position)}
            disabled={!isInLoss || tpOrders.length === 0}
            title="Play Safe"
          >
            PS
          </button>
          <button
            className={`abp-ctrl-btn ${!canSellHalf ? 'disabled' : ''}${restrainMsg ? ' restrain-locked' : ''}`}
            onClick={() => onSellHalf(position)}
            disabled={!canSellHalf}
            title={restrainMsg ?? 'Exit 50%'}
          >
            50%
          </button>
          <button
            className="abp-ctrl-btn flatten"
            onClick={() => onFlatten(position)}
            title="Flatten"
          >
            X
          </button>
        </div>
      </div>
    </div>
  );
};

const OrderRow = ({
  index, style, orders, brokers, onCancelOrder,
}: RowComponentProps<OrderRowProps>) => {
  const order = orders[index];
  const isBuy = order.action === 'Buy';
  const isStop = order.ordType?.toLowerCase().includes('stop');
  const price = isStop ? order.stopPrice : order.price;

  return (
    <div style={style}>
      <div className={`abp-order-row ${isBuy ? 'buy' : 'sell'}`}>
        <div className="abp-order-info">
          <span className="abp-order-broker" title={`${order.brokerId}:${order.accountId}`}>
            {getAccountDisplayName(order.brokerId, order.accountId, brokers)}
          </span>
          <span className={`abp-order-action ${isBuy ? 'buy' : 'sell'}`}>
            {order.action}
          </span>
          <span className="abp-order-qty">{order.orderQty}</span>
          <span className="abp-order-price">
            @{price?.toFixed(2) || '-'}
          </span>
          <span className="abp-order-type">
            {isStop ? 'Stop' : 'Limit'}
          </span>
        </div>
        <button
          className="abp-order-cancel"
          onClick={() => onCancelOrder(order)}
          title="Cancel Order"
        >
          X
        </button>
      </div>
    </div>
  );
};

export const PositionsExpanded: React.FC<PositionsExpandedProps> = ({
  brokers,
  positions,
  orders,
  totalBalance,
  totalPnL,
  currentPrice,
  symbol,
  isPinned,
  onTogglePin,
  onClose,
  onRefresh,
  onFlatten,
  onFlattenAll,
  onCancelOrder,
  onCancelAllOrders,
  onModifyOrder,
  onPartialExit,
  onOpenSettings,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('accounts');
  const [expandedBrokers, setExpandedBrokers] = useState<Set<string>>(
    new Set(brokers.filter(b => b.status === 'connected').map(b => b.id))
  );
  const accounts = useAccountStatsStore(state => state.accounts);

  const connectedBrokers = brokers.filter(b => b.status === 'connected');
  const totalAccounts = connectedBrokers.reduce((sum, b) => sum + (b.accounts?.filter(a => a.enabled !== false).length || 0), 0);

  // Working orders only
  const workingOrders = orders.filter(
    o => o.ordStatus === 'Working' || o.ordStatus === 'Accepted' || o.ordStatus === 'Pending'
  );

  const toggleBrokerExpanded = (brokerId: string) => {
    setExpandedBrokers(prev => {
      const next = new Set(prev);
      if (next.has(brokerId)) {
        next.delete(brokerId);
      } else {
        next.add(brokerId);
      }
      return next;
    });
  };

  // Position controls handlers
  const handleBreakEven = useCallback((position: BrokerPosition) => {
    const { slOrders } = findOrdersForPosition(orders, position);
    if (slOrders.length === 0) return;

    const newPrice = roundToTick(position.netPrice, symbol);
    slOrders.forEach(order => {
      if (order.stopPrice) {
        onModifyOrder(order.id, newPrice, true, order.brokerId, order.accountId, true);
      }
    });
  }, [orders, symbol, onModifyOrder]);

  const handlePlaySafe = useCallback((position: BrokerPosition) => {
    const { tpOrders } = findOrdersForPosition(orders, position);
    if (tpOrders.length === 0) return;

    const newPrice = roundToTick(position.netPrice, symbol);
    tpOrders.forEach(order => {
      if (order.price) {
        onModifyOrder(order.id, newPrice, false, order.brokerId, order.accountId, true);
      }
    });
  }, [orders, symbol, onModifyOrder]);

  const handleSellHalf = useCallback((position: BrokerPosition) => {
    const halfQty = Math.floor(Math.abs(position.netPos) / 2);
    if (halfQty < 1) return;
    onPartialExit(position, halfQty);
  }, [onPartialExit]);

  const positionRowProps = useMemo<PositionRowProps>(() => ({
    positions,
    orders,
    brokers,
    currentPrice,
    symbol,
    onFlatten,
    onBreakEven: handleBreakEven,
    onPlaySafe: handlePlaySafe,
    onSellHalf: handleSellHalf,
  }), [positions, orders, brokers, currentPrice, symbol, onFlatten, handleBreakEven, handlePlaySafe, handleSellHalf]);

  const orderRowProps = useMemo<OrderRowProps>(() => ({
    orders: workingOrders,
    brokers,
    onCancelOrder,
  }), [workingOrders, brokers, onCancelOrder]);


  return (
    <div className={`abp-expanded ${isPinned ? 'pinned' : ''}`}>
      {/* Header */}
      <div className="abp-expanded-header">
        <div className="abp-expanded-stats">
          <div className="abp-stat">
            <span className="abp-stat-label">BALANCE</span>
            <span className="abp-stat-value">{formatCurrency(totalBalance, false)}</span>
          </div>
          <div className="abp-stat">
            <span className="abp-stat-label">P&L</span>
            <span className={`abp-stat-value ${totalPnL >= 0 ? 'positive' : 'negative'}`}>
              {totalPnL >= 0 ? '+' : ''}{formatCurrency(totalPnL)}
            </span>
          </div>
          <div className="abp-stat">
            <span className="abp-stat-label">BROKERS</span>
            <span className="abp-stat-value">{connectedBrokers.length}</span>
          </div>
        </div>
        <div className="abp-expanded-actions">
          <button
            className={`abp-header-btn ${isPinned ? 'active' : ''}`}
            onClick={onTogglePin}
            title={isPinned ? 'Unpin' : 'Pin'}
          >
            {isPinned ? <Pin size={14} /> : <PinOff size={14} />}
          </button>
          <button className="abp-header-btn" onClick={onClose} title="Close">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="abp-tabs">
        <button
          className={`abp-tab ${activeTab === 'positions' ? 'active' : ''}`}
          onClick={() => setActiveTab('positions')}
        >
          Positions
          {positions.length > 0 && <span className="abp-tab-count">{positions.length}</span>}
        </button>
        <button
          className={`abp-tab ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          Orders
          {workingOrders.length > 0 && <span className="abp-tab-count">{workingOrders.length}</span>}
        </button>
        <button
          className={`abp-tab ${activeTab === 'accounts' ? 'active' : ''}`}
          onClick={() => setActiveTab('accounts')}
        >
          Accounts
          {totalAccounts > 0 && <span className="abp-tab-count">{totalAccounts}</span>}
        </button>
        <div className="abp-tabs-actions">
          {onOpenSettings && (
            <button className="abp-tab-action" onClick={onOpenSettings} title="Settings">
              <Settings size={12} />
            </button>
          )}
          <button className="abp-tab-action" onClick={onRefresh} title="Refresh">
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="abp-content">
        {/* Positions Tab */}
        {activeTab === 'positions' && (
          <div className="abp-positions-list">
            {positions.length === 0 ? (
              <div className="abp-empty">No open positions</div>
            ) : (
              <>
                <List
                  rowComponent={PositionRow}
                  rowCount={positions.length}
                  rowHeight={POSITION_ROW_HEIGHT}
                  rowProps={positionRowProps}
                  style={{ height: Math.min(positions.length * POSITION_ROW_HEIGHT, MAX_LIST_HEIGHT) }}
                />
                {positions.length > 1 && (
                  <div className="abp-positions-footer">
                    <button className="abp-action-btn danger" onClick={onFlattenAll}>
                      Flatten All
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="abp-orders-list">
            {workingOrders.length === 0 ? (
              <div className="abp-empty">No working orders</div>
            ) : (
              <>
                <List
                  rowComponent={OrderRow}
                  rowCount={workingOrders.length}
                  rowHeight={ORDER_ROW_HEIGHT}
                  rowProps={orderRowProps}
                  style={{ height: Math.min(workingOrders.length * ORDER_ROW_HEIGHT, MAX_LIST_HEIGHT) }}
                />
                {workingOrders.length > 1 && (
                  <div className="abp-orders-footer">
                    <button className="abp-action-btn danger" onClick={onCancelAllOrders}>
                      Cancel All
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Accounts Tab */}
        {activeTab === 'accounts' && (
          <div className="abp-accounts-list">
            {connectedBrokers.length === 0 ? (
              <div className="abp-empty">No connected brokers</div>
            ) : (
              connectedBrokers.map(broker => {
                const isExpanded = expandedBrokers.has(broker.id);
                const accountBalances = broker.accountBalances || {};

                return (
                  <div key={broker.id} className="abp-broker-section">
                    <button
                      className="abp-broker-header"
                      onClick={() => toggleBrokerExpanded(broker.id)}
                    >
                      {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      <span className="abp-broker-name">{getBrokerPrefix(broker.id)}</span>
                      <span className="abp-broker-count">{broker.accounts?.filter(a => a.enabled !== false).length || 0}</span>
                    </button>
                    {isExpanded && broker.accounts && (
                      <div className="abp-accounts-table">
                        <div className="abp-accounts-header">
                          <span className="col-account">ACCOUNT</span>
                          <span className="col-netliq">NET LIQ</span>
                          <span className="col-pnl">P&L</span>
                          <span className="col-target">TO TARGET</span>
                        </div>
                        {broker.accounts.filter(a => a.enabled !== false).map(account => {
                          const balance: BrokerBalance | undefined = accountBalances[account.id];
                          const accountPnL = balance?.totalPnL ?? 0;

                          // Calculate TO TARGET
                          const statsKey = `${broker.id}:${account.id}`;
                          const stats = accounts.get(statsKey);
                          const profitTarget = stats?.profitTargetEval || null;
                          const startingBalance = stats?.startingBalance || 0;
                          const currentBalance = balance?.netLiq || stats?.currentBalance || 0;
                          const profitSoFar = startingBalance > 0 ? currentBalance - startingBalance : 0;
                          const toTarget = profitTarget ? profitTarget - profitSoFar : null;

                          const isSelected = account.id === broker.selectedAccountId;

                          return (
                            <div key={account.id} className={`abp-account-row ${isSelected ? 'selected' : ''}`}>
                              <span className="col-account" title={account.name}>
                                {shortenAccountName(account.name)}
                              </span>
                              <span className="col-netliq">
                                {formatCurrency(balance?.netLiq, false)}
                              </span>
                              <span className={`col-pnl ${accountPnL >= 0 ? 'positive' : 'negative'}`}>
                                {accountPnL >= 0 ? '+' : ''}{formatCurrency(accountPnL)}
                              </span>
                              <span className={`col-target ${toTarget !== null && toTarget <= 0 ? 'achieved' : ''}`}>
                                {toTarget !== null ? formatCurrency(toTarget) : '-'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};
